import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiUser, FiBook, FiCheck, FiMic, FiSquare, FiArchive, FiRotateCcw } from 'react-icons/fi';
import { adminAPI, API_ORIGIN } from '../../../services/api';

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/** Аудиоплеер с выбором скорости (для проверки записей учеников) */
const AudioPlayerWithSpeed = ({ src, style, defaultSpeed = 1 }) => {
    const audioRef = useRef(null);
    const [speed, setSpeed] = useState(defaultSpeed);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = speed;
        }
    }, [speed]);

    return (
        <div style={{ marginBottom: style?.marginBottom ?? '15px' }}>
            <audio
                ref={audioRef}
                controls
                src={src}
                style={{
                    width: '100%',
                    height: style?.height ?? '35px',
                    maxWidth: style?.maxWidth,
                    display: 'block',
                }}
                onPlay={(e) => {
                    e.currentTarget.playbackRate = speed;
                }}
                onLoadedMetadata={(e) => {
                    e.currentTarget.playbackRate = speed;
                }}
            />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '8px',
                    flexWrap: 'wrap',
                }}
            >
                <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
                    Скорость:
                </span>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {PLAYBACK_SPEEDS.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setSpeed(s)}
                            style={{
                                padding: '3px 8px',
                                fontSize: '12px',
                                borderRadius: '4px',
                                border: speed === s ? '1px solid #4a90e2' : '1px solid #e0e0e0',
                                background: speed === s ? '#e6f4ff' : 'white',
                                color: speed === s ? '#1677ff' : '#555',
                                fontWeight: speed === s ? 600 : 400,
                                cursor: 'pointer',
                                lineHeight: 1.3,
                            }}
                        >
                            {s}x
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AudioCheckingPage = () => {
    const [searchParams] = useSearchParams();
    const initialStudentId = searchParams.get('studentId') || '';
    const [listTab, setListTab] = useState('active');
    const [contentKind, setContentKind] = useState('sentences'); // sentences | texts
    const [recordings, setRecordings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [feedbackStates, setFeedbackStates] = useState({});
    const [filter, setFilter] = useState({ studentId: initialStudentId, lessonId: '' });
    const [students, setStudents] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [savingAll, setSavingAll] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [commentRecordingForId, setCommentRecordingForId] = useState(null);
    const commentRecSessionRef = useRef(null);

    const revokePreviewUrls = (state) => {
        Object.values(state).forEach((f) => {
            if (f?.commentAudioPreviewUrl) {
                URL.revokeObjectURL(f.commentAudioPreviewUrl);
            }
        });
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setSelectedIds([]);
            const [recordingsRes, studentsRes] = await Promise.all([
                adminAPI.getAudioRecordings({
                    ...filter,
                    archived: listTab === 'archive' ? 1 : 0,
                    kind: contentKind,
                }),
                adminAPI.getStudents(),
            ]);
            const recordingsData = recordingsRes.data.recordings || [];
            setRecordings(recordingsData);
            setStudents(studentsRes.data.students || []);

            const feedbacks = {};
            for (const rec of recordingsData) {
                try {
                    const fbRes = await adminAPI.getAudioFeedback(rec.id);
                    if (fbRes.data.feedback) {
                        const fb = fbRes.data.feedback;
                        feedbacks[rec.id] = {
                            rating: fb.rating,
                            comment: fb.comment || '',
                            comment_audio_url: fb.comment_audio_url || null,
                            saved: true,
                            teacher_name: fb.teacher_name,
                            checked_at: fb.checked_at,
                            commentAudioBlob: null,
                            commentAudioPreviewUrl: null,
                        };
                    } else {
                        feedbacks[rec.id] = {
                            rating: 'good',
                            comment: '',
                            comment_audio_url: null,
                            saved: false,
                            commentAudioBlob: null,
                            commentAudioPreviewUrl: null,
                        };
                    }
                } catch (e) {
                    feedbacks[rec.id] = {
                        rating: 'good',
                        comment: '',
                        comment_audio_url: null,
                        saved: false,
                        commentAudioBlob: null,
                        commentAudioPreviewUrl: null,
                    };
                }
            }
            setFeedbackStates((prev) => {
                revokePreviewUrls(prev);
                return feedbacks;
            });
        } catch (error) {
            console.error('Ошибка загрузки данных:', error);
        } finally {
            setLoading(false);
        }
    }, [filter, listTab, contentKind]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const unsavedCount = useMemo(
        () => recordings.filter((r) => !feedbackStates[r.id]?.saved).length,
        [recordings, feedbackStates]
    );

    const reviewedIdsOnPage = useMemo(
        () => recordings.filter((r) => feedbackStates[r.id]?.saved).map((r) => r.id),
        [recordings, feedbackStates]
    );

    const toggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const selectAllOnPage = () => {
        if (selectedIds.length === recordings.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(recordings.map((r) => r.id));
        }
    };

    const selectAllReviewedOnPage = () => {
        setSelectedIds(reviewedIdsOnPage);
    };

    const stopCommentRecording = () => {
        const s = commentRecSessionRef.current;
        if (s?.recorder && s.recorder.state === 'recording') {
            s.recorder.stop();
        }
        commentRecSessionRef.current = null;
        setCommentRecordingForId(null);
    };

    const toggleCommentRecording = (recordingId) => {
        const s = commentRecSessionRef.current;
        if (commentRecordingForId === recordingId && s?.recorder?.state === 'recording') {
            stopCommentRecording();
            return;
        }
        if (s?.recorder?.state === 'recording') {
            stopCommentRecording();
        }

        navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
                const chunks = [];
                const recorder = new MediaRecorder(stream);
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) chunks.push(e.data);
                };
                recorder.onstop = () => {
                    stream.getTracks().forEach((t) => t.stop());
                    const blob = new Blob(chunks, { type: 'audio/webm' });
                    setFeedbackStates((prev) => {
                        const cur = prev[recordingId] || {};
                        if (cur.commentAudioPreviewUrl) {
                            URL.revokeObjectURL(cur.commentAudioPreviewUrl);
                        }
                        const url = URL.createObjectURL(blob);
                        return {
                            ...prev,
                            [recordingId]: {
                                ...cur,
                                commentAudioBlob: blob,
                                commentAudioPreviewUrl: url,
                            },
                        };
                    });
                    commentRecSessionRef.current = null;
                    setCommentRecordingForId(null);
                };
                recorder.start();
                commentRecSessionRef.current = { recorder, stream, chunks };
                setCommentRecordingForId(recordingId);
            })
            .catch(() => {
                alert('Не удалось получить доступ к микрофону');
            });
    };

    const clearPendingCommentAudio = (recordingId) => {
        setFeedbackStates((prev) => {
            const cur = prev[recordingId];
            if (cur?.commentAudioPreviewUrl) {
                URL.revokeObjectURL(cur.commentAudioPreviewUrl);
            }
            return {
                ...prev,
                [recordingId]: {
                    ...cur,
                    commentAudioBlob: null,
                    commentAudioPreviewUrl: null,
                },
            };
        });
    };

    const buildFeedbackFormData = (feedback) => {
        const fd = new FormData();
        fd.append('rating', feedback.rating);
        fd.append('comment', feedback.comment || '');
        if (feedback.commentAudioBlob) {
            fd.append('comment_audio', feedback.commentAudioBlob, 'teacher-comment.webm');
        }
        return fd;
    };

    const submitOneFeedback = async (recordingId, { quiet } = {}) => {
        const feedback = feedbackStates[recordingId];
        if (!feedback) return { ok: false };
        const fd = buildFeedbackFormData(feedback);
        await adminAPI.addAudioFeedback(recordingId, fd);
        if (!quiet) {
            alert('Отзыв сохранен!');
            loadData();
        }
        return { ok: true };
    };

    const handleSubmitFeedback = async (recordingId) => {
        try {
            await submitOneFeedback(recordingId, { quiet: false });
        } catch (error) {
            console.error('Ошибка сохранения отзыва:', error);
            alert('Ошибка при сохранении отзыва');
        }
    };

    const handleSaveAllFeedback = async () => {
        stopCommentRecording();
        const ids = recordings.filter((r) => !feedbackStates[r.id]?.saved).map((r) => r.id);
        if (!ids.length) return;
        setSavingAll(true);
        let ok = 0;
        let fail = 0;
        for (const id of ids) {
            try {
                const feedback = feedbackStates[id];
                if (!feedback) {
                    fail++;
                    continue;
                }
                await adminAPI.addAudioFeedback(id, buildFeedbackFormData(feedback));
                ok++;
            } catch (e) {
                fail++;
            }
        }
        setSavingAll(false);
        alert(
            fail
                ? `Сохранено отзывов: ${ok}. Ошибок: ${fail}.`
                : `Сохранено отзывов: ${ok}.`
        );
        loadData();
    };

    const doArchive = async (ids) => {
        if (!ids.length) return;
        setArchiving(true);
        try {
            await adminAPI.archiveAudioRecordings(ids);
            setSelectedIds([]);
            loadData();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.error || 'Не удалось отправить в архив');
        } finally {
            setArchiving(false);
        }
    };

    const doUnarchive = async (ids) => {
        if (!ids.length) return;
        setArchiving(true);
        try {
            await adminAPI.unarchiveAudioRecordings(ids);
            setSelectedIds([]);
            loadData();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.error || 'Не удалось восстановить');
        } finally {
            setArchiving(false);
        }
    };

    const getRatingColor = (rating) => {
        const colors = {
            excellent: '#52c41a',
            good: '#73d13d',
            needs_improvement: '#faad14',
            poor: '#ff4d4f',
        };
        return colors[rating] || '#999';
    };

    const getRatingName = (rating) => {
        const names = {
            excellent: 'Отлично',
            good: 'Хорошо',
            needs_improvement: 'Нужно улучшить',
            poor: 'Плохо',
        };
        return names[rating] || rating;
    };

    const showFixedSaveBar = listTab === 'active' && unsavedCount > 0;

    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;
    }

    return (
        <div
            style={{
                maxWidth: 1200,
                margin: '0 auto',
                padding: `10px 20px ${showFixedSaveBar ? 88 : 20}px`,
            }}
        >
            <h1 style={{ margin: '0 0 12px 0' }}>Проверка аудиозаписей</h1>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666', lineHeight: 1.5 }}>
                Проверенные записи автоматически попадают в архив через 3 дня после отзыва. В архив можно
                отправить записи вручную — выборочно или все проверенные на странице.
            </p>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onClick={() => setListTab('active')}
                    style={{
                        padding: '10px 18px',
                        borderRadius: '8px',
                        border: listTab === 'active' ? '2px solid #4a90e2' : '1px solid #d9d9d9',
                        background: listTab === 'active' ? '#e6f4ff' : '#fff',
                        fontWeight: listTab === 'active' ? 600 : 500,
                        cursor: 'pointer',
                    }}
                >
                    Активные
                </button>
                <button
                    type="button"
                    onClick={() => setListTab('archive')}
                    style={{
                        padding: '10px 18px',
                        borderRadius: '8px',
                        border: listTab === 'archive' ? '2px solid #4a90e2' : '1px solid #d9d9d9',
                        background: listTab === 'archive' ? '#e6f4ff' : '#fff',
                        fontWeight: listTab === 'archive' ? 600 : 500,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <FiArchive size={16} /> Архив
                </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onClick={() => setContentKind('sentences')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: contentKind === 'sentences' ? '2px solid #52c41a' : '1px solid #d9d9d9',
                        background: contentKind === 'sentences' ? '#f6ffed' : '#fff',
                        fontWeight: contentKind === 'sentences' ? 600 : 500,
                        cursor: 'pointer',
                        fontSize: '14px',
                    }}
                >
                    Предложения
                </button>
                <button
                    type="button"
                    onClick={() => setContentKind('texts')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: contentKind === 'texts' ? '2px solid #722ed1' : '1px solid #d9d9d9',
                        background: contentKind === 'texts' ? '#f9f0ff' : '#fff',
                        fontWeight: contentKind === 'texts' ? 600 : 500,
                        cursor: 'pointer',
                        fontSize: '14px',
                    }}
                >
                    Тексты целиком
                </button>
            </div>

            <div className="card" style={{ marginBottom: '16px', padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Студент</label>
                        <select
                            value={filter.studentId}
                            onChange={(e) => setFilter({ ...filter, studentId: e.target.value })}
                        >
                            <option value="">Все студенты</option>
                            {students.map((student) => (
                                <option key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name} ({student.username})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {listTab === 'active' && recordings.length > 0 && (
                <div
                    className="card"
                    style={{
                        marginBottom: '16px',
                        padding: '14px 18px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={recordings.length > 0 && selectedIds.length === recordings.length}
                            onChange={selectAllOnPage}
                        />
                        <span style={{ fontSize: '14px' }}>Выбрать все на странице</span>
                    </label>
                    <button
                        type="button"
                        disabled={archiving || !selectedIds.length}
                        onClick={() => doArchive(selectedIds)}
                        style={{
                            padding: '8px 14px',
                            background: '#faad14',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: archiving || !selectedIds.length ? 'not-allowed' : 'pointer',
                            opacity: archiving || !selectedIds.length ? 0.6 : 1,
                        }}
                    >
                        В архив выбранные
                    </button>
                    <button
                        type="button"
                        disabled={archiving || !reviewedIdsOnPage.length}
                        onClick={() => {
                            if (
                                !window.confirm(
                                    `Отправить в архив все проверенные на этой странице (${reviewedIdsOnPage.length} шт.)?`
                                )
                            ) {
                                return;
                            }
                            doArchive(reviewedIdsOnPage);
                        }}
                        style={{
                            padding: '8px 14px',
                            background: '#d48806',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: archiving || !reviewedIdsOnPage.length ? 'not-allowed' : 'pointer',
                            opacity: archiving || !reviewedIdsOnPage.length ? 0.6 : 1,
                        }}
                    >
                        В архив все проверенные
                    </button>
                    <button
                        type="button"
                        disabled={archiving || !reviewedIdsOnPage.length}
                        onClick={selectAllReviewedOnPage}
                        style={{
                            padding: '8px 14px',
                            background: '#fff',
                            color: '#4a90e2',
                            border: '1px solid #4a90e2',
                            borderRadius: '6px',
                            fontSize: '13px',
                            cursor: archiving || !reviewedIdsOnPage.length ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Выделить проверенные
                    </button>
                </div>
            )}

            {listTab === 'archive' && recordings.length > 0 && (
                <div
                    className="card"
                    style={{
                        marginBottom: '16px',
                        padding: '14px 18px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={recordings.length > 0 && selectedIds.length === recordings.length}
                            onChange={selectAllOnPage}
                        />
                        <span style={{ fontSize: '14px' }}>Выбрать все</span>
                    </label>
                    <button
                        type="button"
                        disabled={archiving || !selectedIds.length}
                        onClick={() => doUnarchive(selectedIds)}
                        style={{
                            padding: '8px 14px',
                            background: '#4a90e2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: archiving || !selectedIds.length ? 'not-allowed' : 'pointer',
                            opacity: archiving || !selectedIds.length ? 0.6 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        <FiRotateCcw size={14} /> Вернуть выбранные
                    </button>
                </div>
            )}

            <div style={{ display: 'grid', gap: '15px' }}>
                {recordings.length === 0 ? (
                    <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', color: '#999' }}>
                            {listTab === 'archive'
                                ? contentKind === 'texts'
                                    ? 'В архиве нет записей текстов'
                                    : 'Архив пуст'
                                : contentKind === 'texts'
                                  ? 'Нет записей целых текстов для проверки'
                                  : 'Нет аудиозаписей для проверки'}
                        </p>
                    </div>
                ) : (
                    recordings.map((recording) => (
                        <div key={recording.id} className="card" style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                {(listTab === 'active' || listTab === 'archive') && (
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(recording.id)}
                                        onChange={() => toggleSelect(recording.id)}
                                        style={{ marginTop: '4px', width: '18px', height: '18px', flexShrink: 0 }}
                                    />
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '15px',
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    marginBottom: '5px',
                                                }}
                                            >
                                                <FiUser color="#4a90e2" />
                                                <strong>
                                                    {recording.first_name} {recording.last_name}
                                                </strong>
                                                <span style={{ fontSize: '14px', color: '#666' }}>
                                                    ({recording.student_name})
                                                </span>
                                            </div>
                                            {recording.lesson_title && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginBottom: '8px',
                                                        fontSize: '14px',
                                                        color: '#666',
                                                        marginLeft: '24px',
                                                    }}
                                                >
                                                    <FiBook size={14} />
                                                    {recording.lesson_title}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#999', textAlign: 'right' }}>
                                            <div>Запись: {new Date(recording.created_at).toLocaleString('ru-RU')}</div>
                                            {recording.archived_at && (
                                                <div style={{ marginTop: '4px', color: '#d48806' }}>
                                                    В архиве: {new Date(recording.archived_at).toLocaleString('ru-RU')}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {contentKind === 'texts' && (
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                marginBottom: '10px',
                                                padding: '4px 10px',
                                                background: '#f9f0ff',
                                                color: '#722ed1',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Запись текста целиком
                                            {recording.text_title ? `: ${recording.text_title}` : ''}
                                        </div>
                                    )}

                                    {(recording.sentence_en || recording.sentence_ru) && (
                                        <div
                                            style={{
                                                padding: '10px',
                                                background: '#f9f9f9',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                marginBottom: '15px',
                                                maxHeight: contentKind === 'texts' ? '220px' : undefined,
                                                overflowY: contentKind === 'texts' ? 'auto' : undefined,
                                            }}
                                        >
                                            {recording.sentence_en && (
                                                <div
                                                    style={{
                                                        fontWeight: '500',
                                                        marginBottom: recording.sentence_ru ? '5px' : 0,
                                                        whiteSpace: 'pre-wrap',
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {recording.sentence_en}
                                                </div>
                                            )}
                                            {recording.sentence_ru && (
                                                <div
                                                    style={{
                                                        color: '#666',
                                                        fontSize: '13px',
                                                        whiteSpace: 'pre-wrap',
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {recording.sentence_ru}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!recording.sentence_en && !recording.sentence_ru && contentKind === 'texts' && (
                                        <div
                                            style={{
                                                padding: '10px',
                                                background: '#fff7e6',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                color: '#ad6800',
                                                marginBottom: '15px',
                                            }}
                                        >
                                            Текст к записи не найден (возможно, блок удалён). Можно прослушать аудио.
                                        </div>
                                    )}

                                    <AudioPlayerWithSpeed
                                        src={`${API_ORIGIN}${recording.audio_url}`}
                                        style={{ width: '100%', marginBottom: '15px', height: '35px' }}
                                    />

                                    {listTab === 'archive' ? (
                                        <>
                                            {feedbackStates[recording.id]?.saved ? (
                                                <div
                                                    style={{
                                                        padding: '15px',
                                                        background: '#fafafa',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e8e8e8',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'minmax(120px, 200px) 1fr',
                                                            gap: '15px',
                                                            marginBottom: '10px',
                                                        }}
                                                    >
                                                        <div>
                                                            <div
                                                                style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}
                                                            >
                                                                Оценка
                                                            </div>
                                                            <div
                                                                style={{
                                                                    display: 'inline-block',
                                                                    padding: '6px 12px',
                                                                    background: getRatingColor(
                                                                        feedbackStates[recording.id].rating
                                                                    ),
                                                                    color: 'white',
                                                                    borderRadius: '6px',
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                }}
                                                            >
                                                                {getRatingName(feedbackStates[recording.id].rating)}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div
                                                                style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}
                                                            >
                                                                Комментарий
                                                            </div>
                                                            <div style={{ fontSize: '14px', color: '#333' }}>
                                                                {feedbackStates[recording.id].comment || (
                                                                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                                        Нет текста
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {feedbackStates[recording.id].comment_audio_url && (
                                                        <div style={{ marginTop: '12px' }}>
                                                            <div
                                                                style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}
                                                            >
                                                                Голосовой комментарий
                                                            </div>
                                                            <audio
                                                                controls
                                                                style={{ width: '100%', maxWidth: '480px', height: '36px' }}
                                                                src={`${API_ORIGIN}${feedbackStates[recording.id].comment_audio_url}`}
                                                            />
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                                                        {feedbackStates[recording.id].teacher_name} •{' '}
                                                        {new Date(
                                                            feedbackStates[recording.id].checked_at
                                                        ).toLocaleString('ru-RU')}
                                                    </div>
                                                </div>
                                            ) : (
                                                <p style={{ color: '#999', fontSize: '14px' }}>Отзыв не оставлялся</p>
                                            )}
                                            <button
                                                type="button"
                                                disabled={archiving}
                                                onClick={() => doUnarchive([recording.id])}
                                                style={{
                                                    marginTop: '12px',
                                                    padding: '8px 16px',
                                                    background: '#4a90e2',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '13px',
                                                    cursor: archiving ? 'not-allowed' : 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                }}
                                            >
                                                <FiRotateCcw size={14} /> Вернуть в активные
                                            </button>
                                        </>
                                    ) : feedbackStates[recording.id]?.saved ? (
                                        <div>
                                            <div
                                                style={{
                                                    padding: '15px',
                                                    background: '#f6ffed',
                                                    borderRadius: '8px',
                                                    border: '2px solid #52c41a',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginBottom: '12px',
                                                    }}
                                                >
                                                    <FiCheck size={20} color="#52c41a" />
                                                    <strong style={{ color: '#52c41a', fontSize: '15px' }}>
                                                        Отзыв отправлен студенту
                                                    </strong>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'minmax(120px, 200px) 1fr',
                                                        gap: '15px',
                                                        marginBottom: '10px',
                                                    }}
                                                >
                                                    <div>
                                                        <div
                                                            style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}
                                                        >
                                                            Общая оценка
                                                        </div>
                                                        <div
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '6px 12px',
                                                                background: getRatingColor(
                                                                    feedbackStates[recording.id].rating
                                                                ),
                                                                color: 'white',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                fontWeight: '600',
                                                            }}
                                                        >
                                                            {getRatingName(feedbackStates[recording.id].rating)}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div
                                                            style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}
                                                        >
                                                            Комментарий
                                                        </div>
                                                        <div style={{ fontSize: '14px', color: '#333' }}>
                                                            {feedbackStates[recording.id].comment || (
                                                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                                    Без текстового комментария
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {feedbackStates[recording.id].comment_audio_url && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <div
                                                            style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}
                                                        >
                                                            Голосовой комментарий
                                                        </div>
                                                        <audio
                                                            controls
                                                            style={{ width: '100%', maxWidth: '480px', height: '36px' }}
                                                            src={`${API_ORIGIN}${feedbackStates[recording.id].comment_audio_url}`}
                                                        />
                                                    </div>
                                                )}

                                                <div
                                                    style={{
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        marginTop: '10px',
                                                        paddingTop: '10px',
                                                        borderTop: '1px solid #d9f7be',
                                                    }}
                                                >
                                                    Проверил: {feedbackStates[recording.id].teacher_name} •{' '}
                                                    {new Date(feedbackStates[recording.id].checked_at).toLocaleString(
                                                        'ru-RU'
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                disabled={archiving}
                                                onClick={() => doArchive([recording.id])}
                                                style={{
                                                    marginTop: '10px',
                                                    padding: '8px 14px',
                                                    background: '#fff',
                                                    color: '#d48806',
                                                    border: '1px solid #ffc53d',
                                                    borderRadius: '6px',
                                                    fontSize: '13px',
                                                    cursor: archiving ? 'not-allowed' : 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                }}
                                            >
                                                <FiArchive size={14} /> В архив
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px',
                                                padding: '15px',
                                                background: '#f0f8ff',
                                                borderRadius: '8px',
                                                border: '1px solid #d6e4ff',
                                            }}
                                        >
                                            <div
                                                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '15px' }}
                                            >
                                                <div style={{ flex: '0 0 200px', minWidth: '160px' }}>
                                                    <label
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            marginBottom: '5px',
                                                            color: '#666',
                                                        }}
                                                    >
                                                        Общая оценка
                                                    </label>
                                                    <select
                                                        value={feedbackStates[recording.id]?.rating || 'good'}
                                                        onChange={(e) =>
                                                            setFeedbackStates((prev) => ({
                                                                ...prev,
                                                                [recording.id]: {
                                                                    ...prev[recording.id],
                                                                    rating: e.target.value,
                                                                },
                                                            }))
                                                        }
                                                        style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                                    >
                                                        <option value="excellent">Отлично</option>
                                                        <option value="good">Хорошо</option>
                                                        <option value="needs_improvement">Нужно улучшить</option>
                                                        <option value="poor">Плохо</option>
                                                    </select>
                                                </div>

                                                <div style={{ flex: '1', minWidth: '200px' }}>
                                                    <label
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            marginBottom: '5px',
                                                            color: '#666',
                                                        }}
                                                    >
                                                        Текстовый комментарий
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={feedbackStates[recording.id]?.comment || ''}
                                                        onChange={(e) =>
                                                            setFeedbackStates((prev) => ({
                                                                ...prev,
                                                                [recording.id]: {
                                                                    ...prev[recording.id],
                                                                    comment: e.target.value,
                                                                },
                                                            }))
                                                        }
                                                        placeholder="Оставьте комментарий для студента..."
                                                        style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                                    />
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    padding: '12px',
                                                    background: 'white',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e6ebf5',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: '13px',
                                                        fontWeight: '500',
                                                        marginBottom: '8px',
                                                        color: '#666',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                    }}
                                                >
                                                    <FiMic size={16} />
                                                    Аудиокомментарий (по желанию)
                                                </div>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleCommentRecording(recording.id)}
                                                        style={{
                                                            padding: '8px 14px',
                                                            background:
                                                                commentRecordingForId === recording.id ? '#ff4d4f' : '#4a90e2',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                        }}
                                                    >
                                                        {commentRecordingForId === recording.id ? (
                                                            <>
                                                                <FiSquare size={14} /> Остановить запись
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiMic size={14} /> Записать
                                                            </>
                                                        )}
                                                    </button>
                                                    {feedbackStates[recording.id]?.commentAudioPreviewUrl && (
                                                        <>
                                                            <audio
                                                                controls
                                                                style={{
                                                                    height: '32px',
                                                                    flex: '1',
                                                                    minWidth: '200px',
                                                                    maxWidth: '400px',
                                                                }}
                                                                src={feedbackStates[recording.id].commentAudioPreviewUrl}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => clearPendingCommentAudio(recording.id)}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    background: 'transparent',
                                                                    color: '#ff4d4f',
                                                                    border: '1px solid #ffccc7',
                                                                    borderRadius: '6px',
                                                                    fontSize: '13px',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                Удалить запись
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSubmitFeedback(recording.id)}
                                                    style={{
                                                        padding: '8px 20px',
                                                        background: '#52c41a',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                    }}
                                                >
                                                    <FiCheck /> Сохранить
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showFixedSaveBar && (
                <div
                    style={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: '14px 20px',
                        background: 'rgba(255,255,255,0.97)',
                        borderTop: '1px solid #e8e8e8',
                        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '12px',
                    }}
                >
                    <button
                        type="button"
                        disabled={savingAll}
                        onClick={handleSaveAllFeedback}
                        style={{
                            padding: '12px 28px',
                            background: '#52c41a',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: 700,
                            cursor: savingAll ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 8px rgba(82,196,26,0.35)',
                            opacity: savingAll ? 0.85 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <FiCheck size={18} />
                        {savingAll ? 'Сохранение…' : `Сохранить все отзывы (${unsavedCount})`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AudioCheckingPage;
